import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';
import {
  clearPersistedRefreshTokens,
  sessionRefreshStore,
} from 'utils/sessionRefreshStore';
import {
  AuthTokens,
  B2bAccessRequestPayload,
  CustomerProfile,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
} from 'types/auth';

export interface StoreTokenApiResponse {
  accessToken?: string;
  AccessToken?: string;
  refreshToken?: string;
  RefreshToken?: string;
  customerId?: number;
  CustomerId?: number;
  expiresAt?: string;
  ExpiresAt?: string;
  priceListId?: string;
  PriceListId?: string;
  customerType?: string;
  CustomerType?: string;
  tenantCode?: string;
  TenantCode?: string;
}

interface StoreProfileApiResponse {
  customerId?: number;
  CustomerId?: number;
  name?: string;
  Name?: string;
  email?: string;
  Email?: string;
  phone?: string;
  Phone?: string;
  customerType?: string;
  CustomerType?: string;
  priceListId?: string;
  PriceListId?: string;
}

export const mapTokens = (data: StoreTokenApiResponse, emailHint?: string): AuthTokens => {
  const token = data.accessToken || data.AccessToken || '';
  const refreshToken = data.refreshToken || data.RefreshToken || '';
  const customerId = String(data.customerId ?? data.CustomerId ?? '');
  return {
    token,
    refreshToken,
    customerId,
    email: emailHint || '',
    displayName: '',
  };
};

const persistSession = (data: AuthTokens) => {
  clearPersistedRefreshTokens();
  tenantStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
  sessionRefreshStore.set(data.refreshToken);
  tenantStorage.setItem(STORAGE_KEYS.CUSTOMER_ID, data.customerId);
  tenantStorage.setItem(STORAGE_KEYS.EMAIL, data.email);
  tenantStorage.setItem(STORAGE_KEYS.DISPLAY_NAME, data.displayName);
  api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
};

const clearSessionStorage = () => {
  tenantStorage.removeItem(STORAGE_KEYS.TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  tenantStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
  tenantStorage.removeItem(STORAGE_KEYS.EMAIL);
  tenantStorage.removeItem(STORAGE_KEYS.DISPLAY_NAME);
  sessionRefreshStore.clear();
  clearPersistedRefreshTokens();
  delete api.defaults.headers.common.Authorization;
};

const mapProfile = (data: StoreProfileApiResponse): CustomerProfile => {
  const name = (data.name || data.Name || '').trim();
  const parts = name.split(/\s+/);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  const customerTypeRaw = (data.customerType || data.CustomerType || '').toUpperCase();
  return {
    customerId: String(data.customerId ?? data.CustomerId ?? ''),
    email: data.email || data.Email || '',
    firstName,
    lastName,
    phone: data.phone || data.Phone,
    customerType: customerTypeRaw === 'B2B' ? 'B2B' : 'B2C',
  };
};

const authenticationService = {
  login: async (data: LoginRequest): Promise<AuthTokens> => {
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_LOGIN, data);
    const tokens = mapTokens(response.data, data.email);
    persistSession(tokens);
    return tokens;
  },

  register: async (data: RegisterRequest): Promise<AuthTokens> => {
    const name = `${data.firstName} ${data.lastName}`.trim();
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_REGISTER, {
      email: data.email,
      password: data.password,
      name,
    });
    const tokens = mapTokens(response.data, data.email);
    tokens.displayName = name;
    persistSession(tokens);
    return tokens;
  },

  activateAccount: async (data: ResetPasswordRequest): Promise<AuthTokens> => {
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_ACTIVATE, {
      token: data.token,
      newPassword: data.newPassword,
    });
    const tokens = mapTokens(response.data);
    persistSession(tokens);
    return tokens;
  },

  requestB2bAccess: async (data: B2bAccessRequestPayload): Promise<void> => {
    await api.post(ENDPOINTS.AUTH_B2B_REQUEST, data);
  },

  logout: async (): Promise<void> => {
    try {
      const refreshToken = sessionRefreshStore.get();
      if (refreshToken) {
        await api.post(ENDPOINTS.AUTH_LOGOUT, { refreshToken });
      }
    } finally {
      clearSessionStorage();
    }
  },

  refreshToken: async (refreshToken?: string): Promise<AuthTokens> => {
    const tokenToUse = refreshToken || sessionRefreshStore.get();
    if (!tokenToUse) {
      throw new Error('No refresh token available');
    }
    const response = await api.post<StoreTokenApiResponse>(ENDPOINTS.AUTH_REFRESH, {
      refreshToken: tokenToUse,
    });
    const email = tenantStorage.getItem(STORAGE_KEYS.EMAIL) || undefined;
    const tokens = mapTokens(response.data, email);
    persistSession(tokens);
    return tokens;
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<void> => {
    await api.post(ENDPOINTS.AUTH_FORGOT, data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await api.post(ENDPOINTS.AUTH_RESET, data);
  },

  getProfile: async (): Promise<CustomerProfile> => {
    const response = await api.get<StoreProfileApiResponse>(ENDPOINTS.ACCOUNT_PROFILE);
    return mapProfile(response.data);
  },

  updateProfile: async (data: Partial<CustomerProfile>): Promise<CustomerProfile> => {
    const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
    const response = await api.put<StoreProfileApiResponse>(ENDPOINTS.ACCOUNT_PROFILE, {
      name: name || undefined,
      phone: data.phone,
    });
    return mapProfile(response.data);
  },
};

export default authenticationService;
